/**
 * ENG-7909: manager-side writer for the per-agent account-enforcement marker.
 *
 * The /host/agents poll carries a resolved SOFT enforcement level (warn/mute)
 * per agent (see host-runtime.ts). On each poll the manager mirrors it to a tiny
 * marker under the agent's dir (`~/.augmented/<codeName>/account-enforcement.json`)
 * that the channel MCP servers read per admitted human inbound. This is the
 * account-level twin of the usage-limit marker (usage-banner-monitor.ts).
 *
 * `warn`/`mute` write the marker; anything else (`none`, or a `halt` that arrives
 * as level=null + the kill_switch/paused overlay) clears it. Best-effort: a
 * write/clear failure is logged via the injected logger and never throws, so the
 * poll cycle is never broken by a filesystem hiccup.
 */

import {
  ACCOUNT_ENFORCEMENT_MARKER_FILENAME,
  serializeAccountEnforcementMarker,
  type AccountEnforcementLevel,
} from '@augmented/core';
import { mkdirSync, renameSync, rmSync, writeFileSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';

/**
 * The marker path for an agent under `~/.augmented/<codeName>/`. Co-located with
 * the ENG-7904 usage-limit marker — the shared manager↔MCP per-agent seam; the
 * codename path is symlink-compatible under ADR-0049 and is swept alongside
 * usage-limit when the id-keying migration lands.
 */
export function accountEnforcementMarkerPath(codeName: string): string {
  return join(homedir(), '.augmented', codeName, ACCOUNT_ENFORCEMENT_MARKER_FILENAME); // agent-dir-allow: co-located with the usage-limit marker (shared manager↔MCP seam), symlink-compatible under ADR-0049
}

/**
 * Write (warn/mute) or clear (anything else) this agent's account-enforcement
 * marker to match the level from the poll. `log` defaults to a stderr writer so
 * the manager's normal logger can be passed in; failures are swallowed.
 */
export function syncAccountEnforcementMarker(
  codeName: string,
  level: AccountEnforcementLevel | 'none' | null | undefined,
  log: (msg: string) => void = (m) => process.stderr.write(`${m}\n`),
  /**
   * ENG-8541: the operator-configured notice for this level, straight off the
   * poll (`enforcement_text`). Undefined/blank writes no `text` field at all, so
   * an unconfigured platform produces a byte-identical marker to pre-ENG-8541 and
   * the reader falls back to its own built-in default for the level.
   */
  text?: string | null,
): void {
  if (level !== 'warn' && level !== 'mute') {
    clearAccountEnforcementMarker(codeName, log);
    return;
  }
  const dir = join(homedir(), '.augmented', codeName); // agent-dir-allow: co-located with the usage-limit marker (shared manager↔MCP seam), symlink-compatible under ADR-0049
  const path = join(dir, ACCOUNT_ENFORCEMENT_MARKER_FILENAME);
  // Atomic write (temp + rename): the manager rewrites this marker every poll
  // while enforced, and an MCP reader parses it per admitted inbound. A plain
  // writeFileSync truncates-then-writes, so a concurrent reader could observe
  // half-written JSON, parse it to null, and dispatch a muted inbound (fail-OPEN
  // — the wrong direction for an enforcement control). renameSync is atomic
  // within the same directory on POSIX, so the reader only ever sees the old or
  // the new complete file (CodeRabbit).
  const tempPath = `${path}.${process.pid}.tmp`;
  try {
    mkdirSync(dir, { recursive: true });
    writeFileSync(tempPath, serializeAccountEnforcementMarker(level, text), 'utf-8');
    renameSync(tempPath, path);
  } catch (err) {
    // Best-effort clean up a temp file left behind by a failed write/rename so
    // it doesn't accumulate; the enforcement marker itself is unchanged.
    try {
      rmSync(tempPath, { force: true });
    } catch {
      // ignore — the leftover temp is harmless and never read.
    }
    log(`[account-enforcement] marker sync failed for '${codeName}': ${(err as Error).message}`);
  }
}

/** Best-effort remove the per-agent account-enforcement marker. */
export function clearAccountEnforcementMarker(
  codeName: string,
  log: (msg: string) => void = (m) => process.stderr.write(`${m}\n`),
): void {
  const path = accountEnforcementMarkerPath(codeName);
  try {
    rmSync(path, { force: true });
  } catch (err) {
    log(`[account-enforcement] marker clear failed for '${codeName}': ${(err as Error).message}`);
  }
}
